import { useMediaQuery } from "@mui/material";
import CloseIconButton from "components/AppModalDialogsV2/Dialogs/lib/CloseIconButton";
import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import { usePopUpsV2 } from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import useRewardsById from "hooks/useRewardsById";
import { AcceptedFriendReferralTracker } from "interfaces";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
	Container,
	CustomButton,
	DataContainer,
	ItemImage,
	Items,
	Offer,
	OfferItem,
	OffersContainer,
	OfferText,
	RewardAmount,
	RewardAmountContainer,
	RewardItem,
	SubTitle,
	Title
} from "./styles";
import { useImagePath } from "hooks/useImagePath";
import { friendCenterAssets } from "assets/cdn";

interface Props {}

export const FriendInviteAcceptedOrErrorPopup: React.FC<Props> = () => {
	const { getImage } = useImagePath("cdn");
	const navigate = useNavigate();
	const { close, stack } = usePopUpsV2();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const isLandscape = useMediaQuery("(orientation: landscape)");
	const scale = useCalculateScale(isLandscape ? 910 + 40 : 343 + 40, 410 + 40);
	const {
		receivedFirstLoginReward,
		setReceivedFirstLoginReward,
		syncUserTracker
	} = useWebStoreContext();

	const currentPopup = [...stack]
		.reverse()
		.find(({ id }) => id === "friend-invite-accepted-popup");
	const payload = currentPopup?.payload;
	const alreadyAccepted = payload?.alreadyAccepted === true;
	const data = payload?.data as AcceptedFriendReferralTracker | undefined;
	const acceptSuccessful = data?.acceptSuccessful as boolean;
	const acceptedSuccessReferralId = data?.acceptedSuccessReferralId as string;
	const isAlreadyAccepted = !acceptSuccessful || alreadyAccepted;
	const backgroundImage = getImage(
		isLandscape && !isAlreadyAccepted
			? friendCenterAssets.common.confirmPopupBackgroundLandscape
			: friendCenterAssets.common.confirmPopupBackground
	);
	const displayRewards = useRewardsById(data?.rewardId as string);
	const displayExtraRewards = useRewardsById(
		receivedFirstLoginReward?.rewardId as string
	);

	useEffect(() => {
		if (!data) {
			return;
		}
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "dialog_shown",
			event_type: "free_login_gift_dialog",
			action: "dialog_shown",
			source: "free_login_gift_dialog",
			type: "web_friend_referral",
			tag_text: data?.userId,
			reward_id: JSON.stringify([
				data?.rewardId,
				receivedFirstLoginReward?.rewardId
			]),
			reward_type: alreadyAccepted
				? "already_claimed"
				: receivedFirstLoginReward
				? "first_login"
				: "existed_user_back"
		});
	}, [data]);

	const onClose = (cs?: boolean) => {
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "dialog_x_closed",
			event_type: "free_login_gift_dialog",
			action: cs ? "continue_shopping" : "dialog_x_closed",
			source: "free_login_gift_dialog",
			type: "web_friend_referral",
			tag_text: data?.userId,
			reward_id: JSON.stringify([
				data?.rewardId,
				receivedFirstLoginReward?.rewardId
			]),
			reward_type: alreadyAccepted
				? "already_claimed"
				: receivedFirstLoginReward
				? "first_login"
				: "existed_user_back"
		});
		setReceivedFirstLoginReward(null);
		syncUserTracker();
		if (currentPopup) {
			close({ id: currentPopup.id });
		}
		if (cs) {
			navigate("/shop");
		}
	};

	const onClaim = () => {
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "claim_my_reward_click",
			event_type: "free_login_gift_dialog",
			action: "claim_my_reward_click",
			source: "free_login_gift_dialog",
			type: "web_friend_referral",
			tag_text: data?.userId,
			reward_id: JSON.stringify([
				data?.rewardId,
				receivedFirstLoginReward?.rewardId
			]),
			reward_type: alreadyAccepted
				? "already_claimed"
				: receivedFirstLoginReward
				? "first_login"
				: "existed_user_back"
		});
		onClose(true);
	};

	return (
		<Container
			scale={scale}
			alreadyAccepted={isAlreadyAccepted}
			$backgroundImage={backgroundImage}
		>
			<CloseIconButton onClose={onClose} />
			<div>
				<Title>
					{!acceptSuccessful && !acceptedSuccessReferralId ? (
						<>
							Bonus is no
							<br /> longer available
						</>
					) : alreadyAccepted ||
					  (!acceptSuccessful && acceptedSuccessReferralId) ? (
						<>
							You&apos;ve already
							<br /> claimed your
							<br /> invite gift!
						</>
					) : receivedFirstLoginReward ? (
						<>
							{isLandscape ? (
								<>
									Welcome to the Web with
									<br /> Extra Rewards!
								</>
							) : (
								<>
									Welcome to the
									<br /> Web with Extra
									<br /> Rewards!
								</>
							)}
						</>
					) : (
						<>
							{isLandscape ? (
								<>Welcome Back to Web!</>
							) : (
								<>
									Welcome Back
									<br /> to Web!
								</>
							)}
						</>
					)}
				</Title>
				<SubTitle>
					{!acceptSuccessful || alreadyAccepted ? (
						<>
							Invite your friends to web and
							<br /> get more rewards.
						</>
					) : receivedFirstLoginReward ? (
						<>
							{isLandscape ? (
								<>You and your friend both get rewarded.</>
							) : (
								<>
									You and your friend both get
									<br /> rewarded.
								</>
							)}
						</>
					) : (
						<>
							{isLandscape ? (
								<>
									{data?.userName ?? "Your friend"} invited you back — enjoy
									your referral reward!
								</>
							) : (
								<>
									{data?.userName ?? "Your friend"} invited you back
									<br /> — enjoy your referral reward!
								</>
							)}
						</>
					)}
				</SubTitle>
			</div>
			<DataContainer>
				{acceptSuccessful && !alreadyAccepted && (
					<OffersContainer>
						{receivedFirstLoginReward && (
							<Offer>
								<OfferText>Welcome Gift:</OfferText>
								<OfferItem>
									<Items>
										{displayExtraRewards.map(
											({ imageUrl, description }, key) => {
												return (
													<RewardItem
														className="imgWrap"
														key={key}
														width={"25%"}
													>
														<ItemImage src={imageUrl} alt="" />
														<RewardAmountContainer>
															<RewardAmount>{description}</RewardAmount>
														</RewardAmountContainer>
													</RewardItem>
												);
											}
										)}
									</Items>
								</OfferItem>
								<span>+</span>
							</Offer>
						)}
						<Offer>
							<OfferText>Referral Bonus:</OfferText>
							<OfferItem>
								<Items>
									{displayRewards.map(({ imageUrl, description }, key) => {
										return (
											<RewardItem className="imgWrap" key={key} width={"25%"}>
												<ItemImage src={imageUrl} alt="" />
												<RewardAmountContainer>
													<RewardAmount>{description}</RewardAmount>
												</RewardAmountContainer>
											</RewardItem>
										);
									})}
								</Items>
							</OfferItem>
						</Offer>
					</OffersContainer>
				)}
				<CustomButton
					variant="contained"
					themeColor="secondary"
					size="large"
					onClick={onClaim}
				>
					{acceptSuccessful ? "Continue Shopping" : "Okay"}
				</CustomButton>
			</DataContainer>
		</Container>
	);
};
